import React, { FC, useCallback, useState } from 'react';
import { ULContainer } from '../../Common/Layout/ULContainer.styled';
import Navbar from '../../Common/Navbar/Navbar';
import { Box, Button, Heading, Label, List } from '@kite-js/core';
import signup from '../../Services/ajaxService';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { usePartnerTypeSelection } from '../../../hooks/usePartnerTypeSelection';
import SignupSuccess from '../../Common/SignupSuccess/SignupSuccess';
import { PageType } from '../../../constants/common/pageType';
import { CheckBoxMargin, SpanMargin } from '../../Common/CheckBoxMargin/CheckBoxMargin.styled';
import CommonModal from '../../Common/CommonModal/CommonModal';
import SignupTermAndPoicy from '../../Common/SignupTermAndPolicy/SignupTermAndPolicy';
import { CmsIds } from '../../../constants/common/CmsIds';
import { Select as SelectBeta } from '@kite-js/experimental';

interface Props {
    cmsItems: Cms.CmsDictionary;
}

const OnBoarding: FC<Props> = ({ cmsItems }) => {
    const isMobile = useIsMobile();
    const [emailSubscription, setEmailSubscription] = useState<boolean>(false);
    const [agreeTerm, setAgreeTerm] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [partnerTypeError, setPartnerTypeError] = useState<string>('');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState<boolean>(false);
    const [signupError, setSignupError] = useState<boolean>(false);

    const handlePartnerTypeChange = useCallback(() => {
        setAgreeTerm(false);
        setErrorMessage('');
        setPartnerTypeError('');
    }, []);

    const {
        hasPsrcInQuery,
        signUpTypeFromUrl,
        partnerType,
        partnerTypeItems: partnerTypeSelectItems,
        partnerTypeSelectValue,
        handlePartnerTypeSelectChange,
        agreementLabelContent,
    } = usePartnerTypeSelection(cmsItems, {
        onPartnerTypeChange: handlePartnerTypeChange,
    });

    const agreementTouched = submitAttempted || agreeTerm || !!errorMessage;
    const partnerTypeSelectTouched = submitAttempted || !!partnerTypeError;
    const partnerTypeSelectVariant = partnerTypeSelectTouched && partnerTypeError ? 'error' : undefined;

    const submitSignup = async () => {
        setSubmitAttempted(true);
        setPartnerTypeError('');
        setErrorMessage('');

        if (partnerType === null) {
            setPartnerTypeError(cmsItems[CmsIds.PartnerTypeRequired]);
            return;
        }

        if (!agreeTerm) {
            setErrorMessage(cmsItems[CmsIds.PleaseAcknowledge]);
            return;
        }

        const typeForSignup = hasPsrcInQuery ? signUpTypeFromUrl : partnerType!;
        const result = await signup(typeForSignup, emailSubscription, PageType.OnBoarding);
        if (result.status == 200) {
            setSignupSuccess(true);
            setSignupError(false);
        } else {
            setSignupError(true);
        }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailSubscription(event.target.checked);
    };

    const handleAgreement = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgreeTerm(event.target.checked);
        if (event.target.checked) {
            setErrorMessage('');
        }
    };

    return (
        <ULContainer>
            <Navbar cmsItems={cmsItems} />
            <Box
                shadow="M"
                radius="M"
                p="L"
                style={{ margin: 'auto', marginTop: '10%', minWidth: isMobile ? '315px' : '500px' }}
                width="60%"
                background={0}
            >
                <Heading variant="h3" strong={true}>
                    Onboarding
                </Heading>
                <Box mb="L" px="M" style={{ textAlign: 'left' }}>
                    <Box mb="XS">
                        <Label>{cmsItems[CmsIds.PartnerTypeFieldLabel]}</Label>
                    </Box>
                    <SelectBeta
                        title={partnerTypeSelectValue?.label ?? ''}
                        items={partnerTypeSelectItems}
                        multiSelect={false}
                        allowSearch={false}
                        value={partnerTypeSelectValue}
                        onChange={handlePartnerTypeSelectChange}
                        variant={partnerTypeSelectVariant}
                        helperText={partnerTypeError || undefined}
                        data-testid="onboard-partner-type"
                    />
                </Box>
                <Box p="M">
                    <List variant="vertical">
                        <CheckBoxMargin
                            name="agreement"
                            checked={agreeTerm}
                            onChange={handleAgreement}
                            errors={errorMessage || undefined}
                            touched={agreementTouched}
                            inputAlignment="top"
                            disabled={!partnerTypeSelectValue}
                            data-testid="onboard-agreement"
                        >
                            <SpanMargin>{agreementLabelContent}</SpanMargin>
                        </CheckBoxMargin>
                        <CheckBoxMargin name="subscription" onChange={handleEmailChange} touched={true}>
                            <SpanMargin>{cmsItems[CmsIds.RecieveUpdateText]}</SpanMargin>
                        </CheckBoxMargin>
                    </List>
                </Box>
                <Box p="M">
                    <Button onClick={submitSignup} data-testid="onboard-submit">
                        {cmsItems[CmsIds.CreateAccount]}
                    </Button>
                </Box>
                <SignupTermAndPoicy cmsItems={cmsItems} />
            </Box>
            <SignupSuccess
                open={signupSuccess}
                cmsItems={cmsItems}
                signupType={
                    signupSuccess
                        ? (hasPsrcInQuery ? signUpTypeFromUrl : partnerType!)
                        : signUpTypeFromUrl
                }
                aid={0}
            />
            <CommonModal open={signupError} testId="error-modal">
                <Box p="XL">
                    <p dangerouslySetInnerHTML={{ __html: cmsItems[CmsIds.SomethingWrong] }} />
                </Box>
            </CommonModal>
        </ULContainer>
    );
};

export default OnBoarding;
